"use client"
import { useEffect, useRef } from "react"

/**
 * Full-height side-by-side editor for long strings (legal copy especially) that
 * don't fit the rail's inline textarea. EN source on the left (read-only), target
 * on the right (editable). Presentational + reusable — no review-specific logic
 * beyond labels, so the inline editor can adopt it later. Esc cancels, ⌘/Ctrl+Enter
 * approves (as-is when clean, edit-&-approve when dirty).
 */
/** Human-readable tier label. The underlying tier value stays `high-traffic`;
 *  only the display label standardises to "high traffic" (matching the worklist
 *  chips). */
function tierLabel(tier: string): string {
  if (tier === "legal") return "⚖ legal"
  if (tier === "glossary") return "📖 glossary"
  return "⬆ high traffic"
}

function tierAriaLabel(tier: string): string {
  if (tier === "legal") return "Legal tier"
  if (tier === "glossary") return "Glossary tier"
  return "High-traffic tier"
}

export function LargeStringEditor({
  keyName,
  tier,
  enValue,
  value,
  dirty,
  busy,
  blocked,
  onChange,
  onApprove,
  onEditApprove,
  onClose,
}: {
  keyName: string
  tier: string
  enValue: string
  value: string
  dirty: boolean
  busy: boolean
  /** True when the value-QA gate has a blocking error — disables approval. */
  blocked: boolean
  onChange: (v: string) => void
  onApprove: () => void
  onEditApprove: () => void
  onClose: () => void
}) {
  const targetRef = useRef<HTMLTextAreaElement>(null)
  useEffect(() => {
    targetRef.current?.focus()
  }, [])

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Escape") {
      e.preventDefault()
      onClose()
    } else if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault()
      if (busy || blocked) return
      if (dirty) onEditApprove()
      else onApprove()
    }
  }

  return (
    <div
      className="ahead-review-modal-overlay"
      data-ahead-editor-ui
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        className="ahead-review-modal"
        role="dialog"
        aria-label={`Review ${keyName}`}
        onKeyDown={onKeyDown}
      >
        <header className="ahead-review-modal-head">
          <span className="ahead-review-modal-key">{keyName}</span>
          <span
            className="ahead-review-modal-tier"
            data-tier={tier}
            aria-label={tierAriaLabel(tier)}
          >
            {tierLabel(tier)}
          </span>
          <button
            className="ahead-review-modal-x"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </header>
        <div className="ahead-review-modal-cols">
          <label className="ahead-review-modal-col">
            <span>EN source</span>
            <textarea readOnly value={enValue} aria-label="EN source" />
          </label>
          <label className="ahead-review-modal-col">
            <span>Translation</span>
            <textarea
              ref={targetRef}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              aria-label="Translation"
            />
          </label>
        </div>
        <footer className="ahead-review-modal-foot">
          <span className="ahead-review-modal-hint">
            {blocked
              ? "Fix the error above to approve."
              : "⌘↵ to approve · esc to close"}
          </span>
          <span className="ahead-review-modal-spacer" />
          <button disabled={busy || dirty || blocked} onClick={onApprove}>
            Approve as-is
          </button>
          <button disabled={busy || !dirty || blocked} onClick={onEditApprove}>
            Edit &amp; approve
          </button>
        </footer>
      </div>
    </div>
  )
}
